// React & Helper Libraries
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";

// Services
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import { getVehicleGroupVehiclesAPI } from "services/api/calculations/vehiclegroup/api";
import { VehicleGroupVehicleRow } from "services/api/calculations/vehiclegroup/types";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { NoResults } from "ui/components/NoResults";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Custom Components
import VehiclesSearchResults from "./components/VehiclesSearchResults/VehiclesSearchResults";
import VehicleGroupVehiclesEditCard from "../VehicleGroupVehiclesEditCard/VehicleGroupVehiclesEditCard";

// Types
import { VehicleGroupVehiclesCardProps } from "./types";

const VehicleGroupVehiclesCard: React.FC<VehicleGroupVehiclesCardProps> = (
  props
) => {
  const { vehicleGroupId } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleGroupVehicleRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getVehicleGroupVehiclesAPI(
        pagination,
        "",
        vehicleGroupId
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setShowAddNewModal(true);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setShowAddNewModal(false);
  }, []);

  return (
    <>
      <div className="space-y-3 rounded-sm bg-white border">
        <div>
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase ">
              {t("admin.calculation.setting.vehiclegroup.vehicles.title")}
            </p>
            <div className="flex items-center gap-2 px-3 my-3 ">
              <div className=""></div>
              <div>
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.calculation.vehiclegroup.vehicles.add")}
                  onClick={() => setShowAddNewModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
          </div>
          <div className="m-[-1px]">
            {!isLoading && searchResults.pagination.totalPages === 0 ? (
              <NoResults
                message={t(
                  "admin.calculation.setting.vehiclegroup.vehicles.search.noresults"
                )}
              />
            ) : (
              <VehiclesSearchResults
                searchResults={searchResults}
                selectedRows={selectedRows}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                onPaginationChange={handlePaginationChange}
                onSelectedChange={handleSelectedChange}
              />
            )}
          </div>
        </div>
      </div>
      {showAddNewModal && (
        <Modal
          show={showAddNewModal}
          onHide={() => setShowAddNewModal(false)}
          size="full"
        >
          <ModalHeader
            title={t(
              "admin.calculation.setting.vehiclegroup.vehilces.add.title"
            )}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <VehicleGroupVehiclesEditCard
              vehicleGroupId={vehicleGroupId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default memo(VehicleGroupVehiclesCard);
